// src/services/api.js
import axios from 'axios';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { tokenRequest } from '../auth/msalConfig';
import { msalInstance } from '../auth/msalInstance';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:62035/api';

const api = axios.create({ baseURL: BASE_URL });

// ── Request interceptor — attach ID token to every request automatically ──────
// This runs before every API call so there's no need to manually call
// initializeApiAuth() or setAuthToken() anywhere in the app.
api.interceptors.request.use(async (config) => {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) return config; // not signed in — let the request go through unauthenticated

  try {
    const result = await msalInstance.acquireTokenSilent({ ...tokenRequest, account });
    config.headers['Authorization'] = `Bearer ${result.idToken}`;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // Silent acquisition failed — redirect to Entra login
      await msalInstance.acquireTokenRedirect(tokenRequest);
    }
    // For other errors let the request go through — the response interceptor
    // will handle the resulting 401
  }
  return config;
});

// ── Response interceptor — handle expired tokens ──────────────────────────────
api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
        if (account) {
          const result = await msalInstance.acquireTokenSilent({ ...tokenRequest, account });
          originalRequest.headers['Authorization'] = `Bearer ${result.idToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        if (refreshError instanceof InteractionRequiredAuthError) {
          await msalInstance.acquireTokenRedirect(tokenRequest);
        }
      }
    }

    return Promise.reject(error);
  }
);

// ── API calls ─────────────────────────────────────────────────────────────────
export const getMe            = ()                                  => api.get('/auth/me');
export const getFacilities    = (homeFacilityId, isCorporate)       => api.get(`/facility/${homeFacilityId}`, { params: { isCorporate } });
export const getDepartments   = (facilityId)                        => api.get(`/facility/${facilityId}/departments`);
export const getShifts        = (departmentId)                      => api.get(`/facility/departments/${departmentId}/shifts`);
export const getMinDate       = ()                                  => api.get('/payperiod/mindate');
export const getAssociates    = (departmentId, shiftId, date)       => api.get('/facility/associates', { params: { departmentId, shiftId, shiftDate: date } });
export const getAllDepartmentAssociates = (departmentId, shiftId, date) => api.get('/facility/associates/all', { params: { departmentId, shiftId, shiftDate: date } });
export const getEntries       = (departmentId, shiftId, shiftDate)  => api.get('/leadpay/entries', { params: { departmentId, shiftId, shiftDate } });
export const getEntry         = (headerId)                          => api.get(`/leadpay/entries/${headerId}`);
export const getBudget        = (departmentId, shiftId, shiftDate)  => api.get('/leadpay/budget', { params: { departmentId, shiftId, shiftDate } });
export const saveEntry        = (data)                              => api.post('/leadpay/entries', data);
export const deleteEntry      = (id)                                => api.delete(`/leadpay/entries/${id}`);
export const submitEntries    = (data)                              => api.post('/leadpay/submit', data);
export const unsubmitEntry    = (headerId)                          => api.post(`/leadpay/entries/${headerId}/unsubmit`);